import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { emis, std } from '../materials.ts';
import { G, M, V, glowSprite } from '../prims.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The Furrows' chest (docs/LABERINTOS.md), left in a dead end by the old garrison: a box of dark planks with a
 * barrel lid, wrapped in iron bands studded with rivets, iron caps on the corners and a lock plate whose keyhole
 * glows dragon red, so it reads in the dark of the labyrinth from the game's camera. The lid swings open on its
 * hinge at the back (`lid`); open, its inside shows dark and the keyhole goes out (`setOpen`).
 *
 * Cheap: everything of one material is one merged mesh, nine meshes and a glow in all.
 */

const W = 0.62;
const D = 0.4;
const H = 0.3;
/** The barrel lid: a low arch (this share of a half circle) whose chord spans the box's depth. */
const LR = D / 2;
const ARCH = 0.6;

const PLANK_A = std('#5a3b27', { roughness: 0.85 });
const PLANK_B = std('#4a3020', { roughness: 0.85 });
const IRON = std('#666a74', { metalness: 0.45, roughness: 0.45 });
const INSIDE = std('#140e0b', { roughness: 1, bumpScale: 0 });
/** The lid's staves are open shells: both faces drawn, so its inside shows when it stands open. */
const LID_A = std('#5a3b27', { roughness: 0.85, side: THREE.DoubleSide });
const LID_B = std('#4a3020', { roughness: 0.85, side: THREE.DoubleSide });

export interface Chest {
  model: Model;
  /** The lid's hinge: rotate it on x (negative opens it). */
  lid: THREE.Group;
  /** Taken: the keyhole and its glow go out. */
  setOpen(open: boolean): void;
}

const boxAt = (w: number, h: number, d: number, x: number, y: number, z: number): THREE.BufferGeometry =>
  new THREE.BoxGeometry(w, h, d).translate(x, y, z);

/** One merged mesh per material from a list of geometries (none: nothing). */
const merged = (geos: THREE.BufferGeometry[], mat: THREE.Material, parent: THREE.Object3D): THREE.Mesh | null => {
  if (!geos.length) return null;
  const g = mergeGeometries(geos.map((x) => (x.index ? x.toNonIndexed() : x)), false);
  for (const x of geos) x.dispose();
  return M(g, mat, parent);
};

/** Half the arch's angle: how much of a circle the barrel lid takes. */
const A = (ARCH * Math.PI) / 2;
/** The barrel's radius and how far its axis sits under the box's top (the arch's chord spans the depth). */
const R = LR / Math.sin(A);
const DROP = R * Math.cos(A);

/** A piece of the barrel from angle a0 to a1 (0 is straight up), x0 to x1 along the box, at radius r. */
const stave = (a0: number, a1: number, x0: number, x1: number, r: number): THREE.BufferGeometry => {
  // A slice of an open cylinder, laid on its side along x, its axis sunk under the box's top.
  const g = new THREE.CylinderGeometry(r, r, x1 - x0, 4, 1, true, Math.PI / 2 + a0, a1 - a0);
  g.rotateZ(Math.PI / 2);
  g.translate((x0 + x1) / 2, -DROP, 0);
  return g;
};

export const furrowsChest = (o?: BaseOpts): Chest => {
  const c = begin(o, 0.5, 61);
  const { g } = c;
  // The box: walls of three planks each, tones alternating, a hair apart so the seams show dark; a bottom board.
  const planksA: THREE.BufferGeometry[] = [];
  const planksB: THREE.BufferGeometry[] = [];
  const ph = H / 3;
  const t = 0.03;
  for (let i = 0; i < 3; i++) {
    const y = ph * i + ph / 2;
    const into = i % 2 ? planksB : planksA;
    for (const z of [-1, 1]) into.push(boxAt(W, ph - 0.008, t, 0, y, z * (D / 2 - t / 2)));
    for (const x of [-1, 1]) (i % 2 ? planksA : planksB).push(boxAt(t, ph - 0.008, D - 2 * t, x * (W / 2 - t / 2), y, 0));
  }
  planksB.push(boxAt(W, 0.02, D, 0, 0.01, 0));
  // Behind the seams, and the inside an open chest shows: dark, its floor sunk well under the rim.
  merged([boxAt(W - 0.012, H - 0.012, D - 0.012, 0, (H - 0.012) / 2, 0)], INSIDE, g);
  merged(planksA, PLANK_A, g);
  merged(planksB, PLANK_B, g);

  // The iron: two bands around the box, corner caps, a rim along the top and rivets on the bands.
  const iron: THREE.BufferGeometry[] = [];
  const band = 0.05;
  for (const x of [-W * 0.3, W * 0.3]) {
    // Straps down the front and back and under the bottom: never across the mouth.
    for (const z of [-1, 1]) iron.push(boxAt(band, H + 0.01, 0.014, x, H / 2, z * (D / 2 + 0.006)));
    iron.push(boxAt(band, 0.012, D + 0.02, x, 0.006, 0));
    for (const y of [0.06, 0.15, 0.24]) for (const z of [-1, 1]) iron.push(new THREE.SphereGeometry(0.012, 5, 3).translate(x, y, z * (D / 2 + 0.012)));
  }
  for (const x of [-1, 1]) for (const z of [-1, 1]) {
    iron.push(boxAt(0.07, 0.07, 0.07, x * (W / 2 - 0.025), 0.035, z * (D / 2 - 0.025)));
    iron.push(boxAt(0.065, 0.05, 0.065, x * (W / 2 - 0.022), H - 0.02, z * (D / 2 - 0.022)));
  }
  // The rim: a frame round the mouth, so an open chest shows its dark inside.
  iron.push(boxAt(W + 0.02, 0.025, 0.03, 0, H - 0.005, D / 2 - 0.005), boxAt(W + 0.02, 0.025, 0.03, 0, H - 0.005, -D / 2 + 0.005));
  iron.push(boxAt(0.03, 0.025, D, W / 2 - 0.005, H - 0.005, 0), boxAt(0.03, 0.025, D, -W / 2 + 0.005, H - 0.005, 0));
  // The lock plate on the front, under the lid's hasp.
  iron.push(boxAt(0.11, 0.12, 0.02, 0, H - 0.075, D / 2 + 0.012));
  merged(iron, IRON, g);
  // The keyhole: a red ember in the plate.
  const keyMat = emis('#ff8a6e', '#ff4f3a', 1.8).clone();
  const key = M(new THREE.BoxGeometry(0.022, 0.045, 0.01), keyMat, g);
  key.position.set(0, H - 0.08, D / 2 + 0.024);
  // A red halo over it, clear of the lid: the dark's only hint of it from down the corridor.
  const glow = glowSprite(g, '#ff4f3a', 1.1, V(0, H + 0.32, 0.05), 0.4, true);

  // The lid, hinged at the back of the box's top.
  const lid = G(g, 0, H, -D / 2);
  const inLid = G(lid, 0, 0, D / 2);
  const lidPlanks: THREE.BufferGeometry[] = [];
  const lidPlanksB: THREE.BufferGeometry[] = [];
  // The barrel: the arch in five staves along the box, tones alternating, a hair apart.
  const staves = 5;
  for (let i = 0; i < staves; i++) {
    const a0 = -A + (2 * A * i) / staves;
    (i % 2 ? lidPlanksB : lidPlanks).push(stave(a0 + 0.01, a0 + (2 * A) / staves - 0.01, -W / 2, W / 2, R));
  }
  // End boards: the arch's segment closing each side.
  for (const x of [-W / 2 + 0.004, W / 2 - 0.004]) {
    const sh = new THREE.Shape();
    sh.moveTo(-LR, 0);
    sh.absarc(0, -DROP, R, Math.PI / 2 + A, Math.PI / 2 - A, true);
    sh.lineTo(-LR, 0);
    const end = new THREE.ShapeGeometry(sh, 6);
    end.rotateY(Math.PI / 2);
    end.translate(x, 0, 0);
    lidPlanksB.push(end);
  }
  merged(lidPlanks, LID_A, inLid);
  merged(lidPlanksB, LID_B, inLid);
  // The lid's iron: two bands over the barrel with rivets, a rim along its edges, and the hasp over the lock.
  const lidIron: THREE.BufferGeometry[] = [];
  for (const x of [-W * 0.3, W * 0.3]) {
    lidIron.push(stave(-A - 0.02, A + 0.02, x - band / 2, x + band / 2, R + 0.01));
    for (const t of [-A * 0.6, 0, A * 0.6]) lidIron.push(new THREE.SphereGeometry(0.012, 5, 3).translate(x, (R + 0.012) * Math.cos(t) - DROP, (R + 0.012) * Math.sin(t)));
  }
  lidIron.push(boxAt(W + 0.02, 0.02, 0.02, 0, 0.01, D / 2 + 0.002), boxAt(W + 0.02, 0.02, 0.02, 0, 0.01, -D / 2 - 0.002));
  lidIron.push(boxAt(0.06, 0.07, 0.018, 0, 0.005, D / 2 + 0.014));
  merged(lidIron, IRON, inLid);
  // Its inside, seen only when it stands open.
  const under = M(new THREE.PlaneGeometry(W - 0.02, D - 0.02), INSIDE, inLid);
  under.rotation.x = Math.PI / 2;
  under.position.y = 0.002;

  const model = done(c, H + R - DROP);
  return {
    model,
    lid,
    setOpen: (open: boolean): void => {
      keyMat.emissiveIntensity = open ? 0 : 1.8;
      keyMat.color.set(open ? '#2a2b30' : '#ff8a6e');
      glow.visible = !open;
    },
  };
};
